// ABOUTME: Canonical Zod schemas and TypeScript types for the Skylegs API connector.
import { ZdateTime } from '../../../types/common.zod';
import { z } from 'zod';

// Skylegs serializes SQL DECIMAL columns as strings ("102.33") and INT columns as numbers.
// Fields backed by decimals can arrive either way depending on tenant data; the string
// branch is constrained to a decimal literal so non-numeric junk fails at the boundary.
const ZSkylegsDecimal = z.union([
  z.number(),
  z.string().regex(/^-?\d+(\.\d+)?$/),
]);

// =====================================
// CREDENTIALS
// =====================================

export const ZSkylegsCredentialConfig = z.object({
  id: z.string(),
  integration: z.literal('SKYLEGS'),
  operatorId: z.string(),
  url: z.string(),
  username: z.string(),
  password: z.string(),
});

// =====================================
// EMBEDDED RELATIONS (returned by default on /flights)
// =====================================

export const ZSkylegsCategoryRef = z.object({
  id: z.number(),
  name: z.string(),
});

export const ZSkylegsAircraftRef = z.object({
  id: z.number(),
  registration: z.string(),
});

export const ZSkylegsAirportRef = z.object({
  id: z.number(),
  // Skylegs allows up to 7 chars (custom codes); not always strict 4-letter ICAO
  icao: z.string(),
  name: z.string().nullable().optional(),
});

export const ZSkylegsTag = z.object({
  id: z.number(),
  name: z.string().nullable().optional(),
});

// =====================================
// FLIGHT (MissionFlight) — response model
// =====================================

export const ZSkylegsFlightStatus = z.enum([
  'OPEN',
  'PARTIAL',
  'RETAKE',
  'REQUESTED',
  'READY',
  'CHANGED',
  'ACTIVE',
  'TERMINATED',
  'DELAYED',
  'CANCELED',
  'DIVERTED',
  'EMERGENCY',
  'UNKNOWN',
  'COMPLETED',
  'PROCESSED',
  'CHECKED',
  'SECURED',
]);

export const ZSkylegsFlight = z.object({
  id: z.number(),
  mission_id: z.number().nullable(),
  category_id: z.number(),
  aircraft_id: z.number(),
  client_id: z.number().nullable().optional(),
  departure_id: z.number(),
  arrival_id: z.number(),
  actual_arrival_id: z.number().nullable(),
  alternate_id: z.number().nullable(),
  alternate2_id: z.number().nullable(),
  // callsign
  number: z.string().nullable(),
  // leg sequence within the mission
  flight_number: z.number().nullable(),
  mission_flight_identifier: z.string().nullable(),
  status: ZSkylegsFlightStatus,

  // Planned OOOI. Datetimes arrive with non-UTC offsets (e.g. -06:00).
  obt: ZdateTime,
  ibt: ZdateTime,
  tot: ZdateTime,
  ldt: ZdateTime,
  delay: z.number().nullable(),
  calculation_method: z.string().nullable().optional(),
  tentative_times: z.boolean().optional(),
  tentative_pax: z.boolean().optional(),

  // Actual OOOI
  actual_est: ZdateTime.nullable(),
  actual_obt: ZdateTime.nullable(),
  actual_ibt: ZdateTime.nullable(),
  actual_tot: ZdateTime.nullable(),
  actual_ldt: ZdateTime.nullable(),
  actual_eqt: ZdateTime.nullable(),
  domestic_eet: z.string().nullable().optional(),

  // Pax / cargo
  pax: z.number().nullable(),
  actual_pax: z.number().nullable(),
  actual_cargo: ZSkylegsDecimal.nullable().optional(),

  // Distances (read-only)
  air_distance: z.number().nullable().optional(),
  ground_distance: z.number().nullable().optional(),
  actual_distance: z.number().nullable().optional(),

  // Fuel (masses in lbs per vendor docs)
  block_fuel: ZSkylegsDecimal.nullable().optional(),
  planned_fuel: ZSkylegsDecimal.nullable().optional(),
  actual_fuel: ZSkylegsDecimal.nullable().optional(),
  actual_fuel_uplift_before: ZSkylegsDecimal.nullable().optional(),
  fuel_uplift_density_before: ZSkylegsDecimal.nullable().optional(),
  saf_before: ZSkylegsDecimal.nullable().optional(),
  actual_fuel_start: ZSkylegsDecimal.nullable().optional(),
  takeoff_fuel: ZSkylegsDecimal.nullable().optional(),
  landing_fuel: ZSkylegsDecimal.nullable().optional(),
  actual_fuel_end: ZSkylegsDecimal.nullable().optional(),
  actual_fuel_uplift: ZSkylegsDecimal.nullable().optional(),
  fuel_uplift_density_after: ZSkylegsDecimal.nullable().optional(),
  ind_fob_after: ZSkylegsDecimal.nullable().optional(),
  saf_after: ZSkylegsDecimal.nullable().optional(),
  fuel_trip: ZSkylegsDecimal.nullable().optional(),
  fuel_cont: ZSkylegsDecimal.nullable().optional(),
  fuel_taxi: ZSkylegsDecimal.nullable().optional(),
  fuel_fres: ZSkylegsDecimal.nullable().optional(),
  fuel_alt: ZSkylegsDecimal.nullable().optional(),
  fuel_min: ZSkylegsDecimal.nullable().optional(),
  fuel_extra: ZSkylegsDecimal.nullable().optional(),
  fuel_ramp: ZSkylegsDecimal.nullable().optional(),
  fuel_disc: ZSkylegsDecimal.nullable().optional(),

  // Weight & balance
  basic_empty_arm: ZSkylegsDecimal.nullable().optional(),
  basic_empty_weight: ZSkylegsDecimal.nullable().optional(),
  dry_operating_arm: ZSkylegsDecimal.nullable().optional(),
  dry_operating_weight: ZSkylegsDecimal.nullable().optional(),
  traffic_load: ZSkylegsDecimal.nullable().optional(),
  zfm: ZSkylegsDecimal.nullable().optional(),
  max_allowed_tow: ZSkylegsDecimal.nullable().optional(),
  actual_tow: ZSkylegsDecimal.nullable().optional(),
  planned_ldw: ZSkylegsDecimal.nullable().optional(),
  cg_forward_limit: ZSkylegsDecimal.nullable().optional(),
  cg_aft_limit: ZSkylegsDecimal.nullable().optional(),
  cg_actual: ZSkylegsDecimal.nullable().optional(),
  configuration_name: z.string().nullable().optional(),
  crew_dispatched: z.string().nullable().optional(),

  // Emissions (read-only)
  emissions: z.number().nullable().optional(),
  eu_ets_set: z.number().nullable().optional(),

  parking_stand: z.string().nullable().optional(),

  // Oil (uplift + level) per engine and APU
  oil_uplift_e1: ZSkylegsDecimal.nullable().optional(),
  oil_uplift_e2: ZSkylegsDecimal.nullable().optional(),
  oil_uplift_e3: ZSkylegsDecimal.nullable().optional(),
  oil_uplift_e4: ZSkylegsDecimal.nullable().optional(),
  oil_uplift_apu: ZSkylegsDecimal.nullable().optional(),
  oil_e1: ZSkylegsDecimal.nullable().optional(),
  oil_e2: ZSkylegsDecimal.nullable().optional(),
  oil_e3: ZSkylegsDecimal.nullable().optional(),
  oil_e4: ZSkylegsDecimal.nullable().optional(),
  oil_apu: ZSkylegsDecimal.nullable().optional(),

  // Deicing
  deicing_type: z.string().nullable().optional(),
  deicing_count: z.number().nullable().optional(),
  deicing_percentage: z.number().nullable().optional(),
  deicing_amount: ZSkylegsDecimal.nullable().optional(),
  deicing_time: z.number().nullable().optional(),

  // Counters / totals
  clock_start: ZSkylegsDecimal.nullable().optional(),
  clock_end: ZSkylegsDecimal.nullable().optional(),
  apu_clock_start: ZSkylegsDecimal.nullable().optional(),
  apu_clock_end: ZSkylegsDecimal.nullable().optional(),
  cycles: ZSkylegsDecimal.nullable().optional(),
  cycles_delta: ZSkylegsDecimal.nullable().optional(),
  cycles_special: ZSkylegsDecimal.nullable().optional(),
  cycles_special2: ZSkylegsDecimal.nullable().optional(),
  cycles_special_delta: ZSkylegsDecimal.nullable().optional(),
  cycles_special2_delta: ZSkylegsDecimal.nullable().optional(),
  apu_cycles: z.number().nullable().optional(),
  apu_cycles_delta: z.number().nullable().optional(),
  landings: z.number().nullable().optional(),
  landings_delta: z.number().nullable().optional(),
  takeoffs_day: z.number().nullable().optional(),
  landings_day: z.number().nullable().optional(),
  takeoffs_night: z.number().nullable().optional(),
  landings_night: z.number().nullable().optional(),
  cruise_level: z.number().nullable().optional(),

  // Ops misc
  flight_rules: z.string().nullable().optional(),
  approaches: z.number().nullable().optional(),
  jumps: z.number().nullable().optional(),
  drops: z.number().nullable().optional(),
  hold: z.number().nullable().optional(),
  track: z.number().nullable().optional(),
  nvgo: z.number().nullable().optional(),
  oxygen: ZSkylegsDecimal.nullable().optional(),
  imc_time: z.string().nullable().optional(),
  ifr_time: z.string().nullable().optional(),
  atl_number: z.string().nullable().optional(),

  // Tyre pressures
  nlg_1: ZSkylegsDecimal.nullable().optional(),
  nlg_2: ZSkylegsDecimal.nullable().optional(),
  mlg_1: ZSkylegsDecimal.nullable().optional(),
  mlg_2: ZSkylegsDecimal.nullable().optional(),
  mlg_3: ZSkylegsDecimal.nullable().optional(),
  mlg_4: ZSkylegsDecimal.nullable().optional(),

  // Crew (read-only seat codes, e.g. "BOB MSC")
  crew_string: z.string().nullable().optional(),
  sign_in: ZdateTime.nullable().optional(),
  sign_out: ZdateTime.nullable().optional(),

  // Text
  remarks: z.string().nullable().optional(),
  handover: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  ftl_remarks: z.string().nullable().optional(),

  // Flags
  publish: z.boolean().optional(),
  empty_leg: z.boolean().optional(),
  shared_leg: z.boolean().optional(),

  // External system references
  rocket_route_plan_id: z.number().nullable().optional(),
  crewbriefing_id: z.number().nullable().optional(),
  foreflight_id: z.string().nullable().optional(),
  cbp_iapi_status: z.string().nullable().optional(),
  cbp_iapi_message: z.string().nullable().optional(),
  cbp_iapi_href: z.string().nullable().optional(),
  cbp_sanctions_status: z.string().nullable().optional(),
  cbp_sanctions_href: z.string().nullable().optional(),
  cbp_submission_status: z.string().nullable().optional(),
  cbp_submission_message: z.string().nullable().optional(),
  cbp_submission_href: z.string().nullable().optional(),

  // Audit
  processed_at: ZdateTime.nullable().optional(),
  created_at: ZdateTime.nullable().optional(),
  updated_at: ZdateTime.nullable().optional(),
  deleted_at: ZdateTime.nullable().optional(),
  created_by: z.number().nullable().optional(),
  updated_by: z.number().nullable().optional(),
  deleted_by: z.number().nullable().optional(),

  // Embedded relations (present on list and single GETs by default)
  category: ZSkylegsCategoryRef.nullable().optional(),
  aircraft: ZSkylegsAircraftRef.nullable().optional(),
  departure: ZSkylegsAirportRef.nullable().optional(),
  arrival: ZSkylegsAirportRef.nullable().optional(),
  actual_arrival: ZSkylegsAirportRef.nullable().optional(),
  alternate: ZSkylegsAirportRef.nullable().optional(),
  alternate2: ZSkylegsAirportRef.nullable().optional(),
  tags: z.array(ZSkylegsTag).optional(),
});

// =====================================
// MISSION (trip) — response model
// =====================================

export const ZSkylegsMission = z.object({
  id: z.number(),
  category_id: z.number(),
  aircraft_id: z.number().nullable(),
  client_id: z.number().nullable(),
  contact_id: z.number().nullable().optional(),
  sales_rep_id: z.number().nullable().optional(),
  occ_rep_id: z.number().nullable().optional(),
  crew_rep_id: z.number().nullable().optional(),
  start: ZdateTime,
  end: ZdateTime,
  // user-facing trip key shown across the Skylegs UI (e.g. "23456885")
  number: z.string().nullable(),
  ref_number: z.string().nullable(),
  crew_string: z.string().nullable().optional(),
  remarks: z.string().nullable().optional(),
  // key of the mission's UI deep link: {ui-base}/mission/{hash}
  hash: z.string().optional(),
  created_at: ZdateTime.nullable().optional(),
  updated_at: ZdateTime.nullable().optional(),
  deleted_at: ZdateTime.nullable().optional(),
  created_by: z.number().nullable().optional(),
  updated_by: z.number().nullable().optional(),
  deleted_by: z.number().nullable().optional(),
  // Mission categories are a separate set from flight categories and include
  // non-trip kinds (e.g. "Confirmed", "Maintenance").
  category: ZSkylegsCategoryRef.nullable().optional(),
  client: z
    .object({ id: z.number(), name: z.string().nullable().optional() })
    .nullable()
    .optional(),
  aircraft: ZSkylegsAircraftRef.nullable().optional(),
  tags: z.array(ZSkylegsTag).optional(),
});

export const ZSkylegsPaginatedMissions = z.object({
  current_page: z.number(),
  data: z.array(ZSkylegsMission),
  from: z.number().nullable(),
  to: z.number().nullable(),
  total: z.number(),
  last_page: z.number(),
  per_page: z.number(),
  path: z.string(),
  next_page_url: z.string().nullable(),
  prev_page_url: z.string().nullable(),
});

// =====================================
// CREW AGENDA (flight crew assignment) — response model
// =====================================

export const ZSkylegsUserAgenda = z.object({
  id: z.number(),
  user_id: z.number(),
  mission_id: z.number().nullable(),
  category_id: z.number().nullable(),
  agendable_id: z.number(),
  agendable_type: z.string(),
  start: ZdateTime,
  end: ZdateTime,
  homebase_id: z.number().nullable(),
  position: z.enum(['left', 'right', 'other']).nullable(),
  // free text per assignment: "Captain", "Commander", "Commander EHS", ...
  kind: z.string().nullable(),
  pilot_in_command: z.boolean(),
  pilot_flying: z.boolean(),
  pilot_takeoff: z.boolean(),
  duty: z.boolean(),
  flightduty: z.boolean(),
  eodp: z.boolean(),
  available: z.boolean().optional(),
  temporary: z.boolean().optional(),
  sort_key: z.number().nullable().optional(),
  sign_in: ZdateTime.nullable().optional(),
  sign_out: ZdateTime.nullable().optional(),
  ks_scale: z.number().nullable().optional(),
  passport_id: z.number().nullable().optional(),
  passport2_id: z.number().nullable().optional(),
  title: z.string().nullable().optional(),
  remarks: z.string().nullable().optional(),
  iapi_status: z.string().nullable().optional(),
  iapi_message: z.string().nullable().optional(),
  sanctions_status: z.string().nullable().optional(),
  sanction_factor: z.number().nullable().optional(),
  created_at: ZdateTime.nullable().optional(),
  updated_at: ZdateTime.nullable().optional(),
  deleted_at: ZdateTime.nullable().optional(),
  created_by: z.number().nullable().optional(),
  updated_by: z.number().nullable().optional(),
  deleted_by: z.number().nullable().optional(),
  category: ZSkylegsCategoryRef.nullable().optional(),
  homebase: ZSkylegsAirportRef.nullable().optional(),
});

export const ZSkylegsPaginatedUserAgendas = z.object({
  current_page: z.number(),
  data: z.array(ZSkylegsUserAgenda),
  from: z.number().nullable(),
  to: z.number().nullable(),
  total: z.number(),
  last_page: z.number(),
  per_page: z.number(),
  path: z.string(),
  next_page_url: z.string().nullable(),
  prev_page_url: z.string().nullable(),
});

// =====================================
// ORGANISATION USER — response model
// =====================================

// Organisation membership of a user. The seat code used in flight `crew_string`
// and the email TechLog matches users on both live here, not on the user record.
// Observed statuses are "active", "inactive" and "pending" (an outstanding
// invitation), on both the membership and the user record; the vendor's set is
// not known to be closed. Status stays a free string because the list is fetched
// whole for every sync, so one unrecognised member would otherwise fail every
// flight of the cycle. Consumers require exactly "active" to use a membership,
// so an unfamiliar status makes that member unusable, not the list unreadable.
export const ZSkylegsUserOrganisationMembership = z.object({
  code: z.string().nullable(),
  email: z.string().nullable(),
  status: z.string(),
  homebase_id: z.number().nullable().optional(),
});

export const ZSkylegsUser = z.object({
  id: z.number(),
  first_name: z.string(),
  middle_name: z.string().nullable().optional(),
  last_name: z.string(),
  status: z.string(),
  pivot: ZSkylegsUserOrganisationMembership,
});

// =====================================
// CATEGORY — response model
// =====================================

// Categories are shared across entities and keyed by `kind`; flight categories
// are kind "MissionFlight". Kind stays a free string because tenants return kinds
// the vendor spec does not enumerate (e.g. "Finding" on the Eagle tenant), and one
// unknown kind must not fail the whole lookup list.
export const ZSkylegsCategory = z.object({
  id: z.number(),
  name: z.string(),
  kind: z.string(),
  utilization: z.string().nullable().optional(),
});

// =====================================
// FLIGHT — write models
// =====================================

// Post-flight data pushed from TechLog totals onto an existing Skylegs flight.
// All fuel masses in lbs, oil uplifts in quarts-equivalent vendor units, deicing_type is "1"-"4".
export const ZSkylegsFlightPostFlightInput = z.object({
  // Flight category, resolved from GET /categories by name among kind "MissionFlight".
  category_id: z.number().nullable().optional(),
  // Integer flight level: 390 = FL390.
  cruise_level: z.number().nullable().optional(),
  actual_obt: ZdateTime.nullable().optional(),
  actual_tot: ZdateTime.nullable().optional(),
  actual_ldt: ZdateTime.nullable().optional(),
  actual_ibt: ZdateTime.nullable().optional(),
  actual_pax: z.number().nullable().optional(),
  actual_cargo: z.number().nullable().optional(),
  // `landings`/`cycles`/`apu_cycles` are cumulative running totals carried
  // forward from the aircraft's previous flight; the `_delta` twins are the
  // per-flight increments. Skylegs keeps them as independent columns: it
  // validates a delta on update but does not recompute the total from it.
  landings: z.number().nullable().optional(),
  landings_delta: z.number().int().nullable().optional(),
  takeoffs_day: z.number().nullable().optional(),
  landings_day: z.number().nullable().optional(),
  takeoffs_night: z.number().nullable().optional(),
  landings_night: z.number().nullable().optional(),
  approaches: z.number().nullable().optional(),
  actual_fuel: z.number().nullable().optional(),
  actual_fuel_uplift_before: z.number().nullable().optional(),
  fuel_uplift_density_before: z.number().nullable().optional(),
  saf_before: z.number().nullable().optional(),
  actual_fuel_start: z.number().nullable().optional(),
  actual_fuel_end: z.number().nullable().optional(),
  actual_fuel_uplift: z.number().nullable().optional(),
  fuel_uplift_density_after: z.number().nullable().optional(),
  saf_after: z.number().nullable().optional(),
  takeoff_fuel: z.number().nullable().optional(),
  landing_fuel: z.number().nullable().optional(),
  ind_fob_after: z.number().nullable().optional(),
  oil_uplift_e1: z.number().nullable().optional(),
  oil_uplift_e2: z.number().nullable().optional(),
  oil_uplift_e3: z.number().nullable().optional(),
  oil_uplift_e4: z.number().nullable().optional(),
  oil_uplift_apu: z.number().nullable().optional(),
  oil_e1: z.number().nullable().optional(),
  oil_e2: z.number().nullable().optional(),
  oil_e3: z.number().nullable().optional(),
  oil_e4: z.number().nullable().optional(),
  oil_apu: z.number().nullable().optional(),
  deicing_type: z.enum(['1', '2', '3', '4']).nullable().optional(),
  deicing_count: z.number().nullable().optional(),
  deicing_time: z.number().nullable().optional(),
  deicing_amount: z.number().nullable().optional(),
  deicing_percentage: z.number().nullable().optional(),
  cycles: z.number().nullable().optional(),
  // Skylegs validates the airframe cycle increment as a number and the APU one
  // as an integer.
  cycles_delta: z.number().nullable().optional(),
  apu_cycles: z.number().nullable().optional(),
  apu_cycles_delta: z.number().int().nullable().optional(),
  clock_start: z.number().nullable().optional(),
  clock_end: z.number().nullable().optional(),
  apu_clock_start: z.number().nullable().optional(),
  apu_clock_end: z.number().nullable().optional(),
  atl_number: z.string().nullable().optional(),
  remarks: z.string().max(500).nullable().optional(),
  notes: z.string().max(3000).nullable().optional(),
  // The flight's whole crew as ordered seat codes, first = left seat. Part of
  // the caller's contract for the update even though Skylegs ignores
  // `crew_string` in the body: the client routes it to the `set_crew_string`
  // query action, which sets the whole list rather than the seats it names. An
  // empty string clears the flight's crew, so the empty case is rejected rather
  // than sent.
  crew_string: z.string().min(1).nullable().optional(),
});

// =====================================
// PAGINATION ENVELOPES
// =====================================

// The part of the default (offset) paginator's envelope a page walk depends on,
// whatever records the page carries. Without `last_page` a walk cannot know
// whether it has seen the whole list.
export const ZSkylegsOffsetPage = z.object({
  data: z.array(z.unknown()),
  last_page: z.number(),
});

// Default (offset) paginator — most verbose, includes total.
export const ZSkylegsPaginatedFlights = z.object({
  current_page: z.number(),
  data: z.array(ZSkylegsFlight),
  from: z.number().nullable(),
  to: z.number().nullable(),
  total: z.number(),
  last_page: z.number(),
  per_page: z.number(),
  path: z.string(),
  next_page_url: z.string().nullable(),
  prev_page_url: z.string().nullable(),
});

// Cursor paginator — preferred for large datasets.
export const ZSkylegsCursorPaginatedFlights = z.object({
  data: z.array(ZSkylegsFlight),
  path: z.string(),
  per_page: z.number(),
  next_cursor: z.string().nullable(),
  next_page_url: z.string().nullable(),
  prev_cursor: z.string().nullable(),
  prev_page_url: z.string().nullable(),
});

// =====================================
// ERRORS
// =====================================

// 422 validation errors: { message, errors: { field: ["message", ...] } }
export const ZSkylegsValidationErrorResponse = z.object({
  message: z.string(),
  errors: z.record(z.array(z.string())),
});

// 401/404/429 bodies: { message }
export const ZSkylegsErrorMessage = z.object({
  message: z.string(),
});

// =====================================
// TYPE EXPORTS (canonical types via z.infer)
// =====================================

export type SkylegsCredentialConfig = z.infer<typeof ZSkylegsCredentialConfig>;
export type SkylegsCategoryRef = z.infer<typeof ZSkylegsCategoryRef>;
export type SkylegsAircraftRef = z.infer<typeof ZSkylegsAircraftRef>;
export type SkylegsAirportRef = z.infer<typeof ZSkylegsAirportRef>;
export type SkylegsTag = z.infer<typeof ZSkylegsTag>;
export type SkylegsFlightStatus = z.infer<typeof ZSkylegsFlightStatus>;
export type SkylegsFlight = z.infer<typeof ZSkylegsFlight>;
export type SkylegsMission = z.infer<typeof ZSkylegsMission>;
export type SkylegsPaginatedMissions = z.infer<
  typeof ZSkylegsPaginatedMissions
>;
export type SkylegsUserAgenda = z.infer<typeof ZSkylegsUserAgenda>;
export type SkylegsUserOrganisationMembership = z.infer<
  typeof ZSkylegsUserOrganisationMembership
>;
export type SkylegsUser = z.infer<typeof ZSkylegsUser>;
export type SkylegsCategory = z.infer<typeof ZSkylegsCategory>;
export type SkylegsPaginatedUserAgendas = z.infer<
  typeof ZSkylegsPaginatedUserAgendas
>;
export type SkylegsFlightPostFlightInput = z.infer<
  typeof ZSkylegsFlightPostFlightInput
>;
export type SkylegsPaginatedFlights = z.infer<typeof ZSkylegsPaginatedFlights>;
export type SkylegsCursorPaginatedFlights = z.infer<
  typeof ZSkylegsCursorPaginatedFlights
>;
export type SkylegsValidationErrorResponse = z.infer<
  typeof ZSkylegsValidationErrorResponse
>;
export type SkylegsErrorMessage = z.infer<typeof ZSkylegsErrorMessage>;
